import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import CustomDataTable from "../../components/customTable/CustomDataTable";
import CustomButton from "../../components/CommonButton/CommonButton";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getFormattedDate } from "../../utils/commonFunctions";
import Loader from "../../components/loader/Loader";
import RefreshButton from "../../components/RefreshButton/RefreshButton";

interface RewardleType {
  Id: number;
  RewardleTypeName: string;
}

interface RewardItem {
  Id: number;
  Description: string;
  Points: number;
  ExpirationInDays: number;
  IsActive?: boolean | number | null;
  DateCreated?: string;
  LastModifiedDate?: string;
  RewardleTypeName?: string;
}

const ManageRewards: React.FC = () => {
  const { businessId } = useParams<any>();
  const [rewards, setRewards] = useState<RewardItem[]>([]);
  const [rewardleTypes, setRewardleTypes] = useState<RewardleType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState({
    message: "",
    type: "success" as "success" | "error" | "info" | "warning"
  });

  // Loading state for confirmation modal operations
  const [isConfirmModalLoading, setIsConfirmModalLoading] =
    useState<boolean>(false);

  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [formValues, setFormValues] = useState<{
    Id?: number;
    Description: string;
    Points: string;
    ExpirationInDays: string;
    IsActive?: number;
    RewardleTypeId: number;
  }>({
    Description: "",
    Points: "",
    ExpirationInDays: "",
    IsActive: 1,
    RewardleTypeId: 6
  });

  // Confirmation modal states
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    type: "activate" | "deactivate";
    reward: RewardItem | null;
    title: string;
    message: string;
  }>({
    open: false,
    type: "activate",
    reward: null,
    title: "",
    message: ""
  });

  const headers = useMemo(
    () => ({
      Authorization: `bearer ${localStorage.getItem("accessToken")}`,
      "Content-Type": "application/json"
    }),
    []
  );

  const fetchRewardleTypes = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/loyaltyprogram/support/rewardletypes`,
        { headers }
      );

      const types: RewardleType[] = Array.isArray(response?.data)
        ? response.data
        : [];

      console.log("Rewardle types data:", types);
      setRewardleTypes(types || []);
    } catch (error) {
      console.error("Error fetching rewardle types:", error);
      setNotificationMessage({
        message: "Failed to fetch reward types",
        type: "error"
      });
    }
  }, [headers]);

  const fetchRewards = useCallback(async () => {
    if (!businessId) return;
    try {
      setIsLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/loyaltyprogram/support/business/rewards/${businessId}`,
        { headers }
      );

      const list: RewardItem[] = Array.isArray(response?.data)
        ? response.data
        : response?.data?.Rewards || [];

      // Ensure each row has an id property for MUI DataGrid
      const rowsWithId = list.map((row) => ({
        ...row,
        id: row.Id // Add lowercase id property
      }));

      console.log("Rewards data:", rowsWithId);
      setRewards(rowsWithId || []);
    } catch (error) {
      console.error("Error fetching rewards:", error);
      setNotificationMessage({
        message: "Failed to fetch rewards",
        type: "error"
      });
    } finally {
      setIsLoading(false);
    }
  }, [businessId, headers]);

  useEffect(() => {
    fetchRewards();
    fetchRewardleTypes();
  }, [fetchRewards, fetchRewardleTypes]);

  const resetForm = () => {
    setFormMode("create");
    setFormValues({
      Description: "",
      Points: "",
      ExpirationInDays: "",
      IsActive: 1,
      RewardleTypeId: 6
    });
    setNotificationMessage({ message: "", type: "success" });
  };

  const handleOpenCreate = () => {
    resetForm();
    setOpenModal(true);
  };

  const handleOpenEdit = (item: RewardItem) => {
    setFormMode("edit");
    // Find the rewardle type ID based on the RewardleTypeName
    const rewardleType = rewardleTypes.find(
      (type) => type.RewardleTypeName === item.RewardleTypeName
    );
    setFormValues({
      Id: item.Id,
      Description: item.Description || "",
      Points: String(item.Points ?? ""),
      ExpirationInDays: String(item.ExpirationInDays ?? ""),
      IsActive: item.IsActive === 1 ? 1 : 0,
      RewardleTypeId: rewardleType?.Id || 6
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    resetForm();
  };

  const handleSave = async () => {
    if (!businessId) return;
    if (
      !formValues.Description ||
      !formValues.Points ||
      !formValues.ExpirationInDays ||
      (formMode === "create" && !formValues.RewardleTypeId)
    ) {
      setNotificationMessage({
        message: "Please fill all required fields",
        type: "warning"
      });
      return;
    }

    setIsModalLoading(true);
    try {
      if (formMode === "create") {
        const payload = {
          BusinessId: businessId,
          Description: formValues.Description,
          Points: Number(formValues.Points),
          ExpirationInDays: Number(formValues.ExpirationInDays),
          RewardleTypeId: formValues.RewardleTypeId
        };
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/loyaltyprogram/support/business/reward`,
          payload,
          { headers }
        );
        setNotificationMessage({ message: "Reward created", type: "success" });
      } else {
        const payload = {
          Id: formValues.Id,
          BusinessId: businessId,
          Description: formValues.Description,
          Points: Number(formValues.Points),
          ExpirationInDays: Number(formValues.ExpirationInDays),
          IsActive: formValues?.IsActive || 0,
          RewardleTypeId: formValues.RewardleTypeId
        };
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/loyaltyprogram/support/business/reward`,
          payload,
          { headers }
        );
        setNotificationMessage({ message: "Reward updated", type: "success" });
      }

      await fetchRewards();
      handleCloseModal();
    } catch (error) {
      console.error("Error saving reward:", error);
      setNotificationMessage({
        message: "Failed to save reward",
        type: "error"
      });
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleToggleStatus = async (reward: RewardItem) => {
    if (!businessId) return;

    const isCurrentlyActive = reward.IsActive === 1;
    const actionType = isCurrentlyActive ? "deactivate" : "activate";

    setConfirmModal({
      open: true,
      type: actionType,
      reward: reward,
      title: `${actionType === "activate" ? "Activate" : "Deactivate"} Reward`,
      message: `Are you sure you want to ${actionType} the reward "${reward.Description}"?`
    });
  };

  const handleConfirmToggleStatus = async () => {
    if (!confirmModal.reward || !businessId) return;

    setIsConfirmModalLoading(true);

    try {
      const newStatus = confirmModal.reward.IsActive === 1 ? 0 : 1;
      const payload = {
        Id: confirmModal.reward.Id,
        BusinessId: businessId,
        Description: confirmModal.reward.Description,
        Points: confirmModal.reward.Points,
        ExpirationInDays: confirmModal.reward.ExpirationInDays,
        IsActive: newStatus
      };

      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/loyaltyprogram/support/business/reward`,
        payload,
        { headers }
      );

      setNotificationMessage({
        message: `Reward ${
          newStatus === 1 ? "activated" : "deactivated"
        } successfully`,
        type: "success"
      });
      setConfirmModal({ ...confirmModal, open: false });
      await fetchRewards();
    } catch (error) {
      console.error("Error toggling reward status:", error);
      setNotificationMessage({
        message: "Failed to update reward status",
        type: "error"
      });
    } finally {
      setIsConfirmModalLoading(false);
    }
  };

  const handleCloseConfirmModal = () => {
    setConfirmModal({ ...confirmModal, open: false });
  };

  const columns: any = [
    {
      field: "Description",
      headerName: "Description",
      width: 200,
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    },
    {
      field: "Points",
      headerName: "Points",
      width: 100,
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    },
    {
      field: "ExpirationInDays",
      headerName: "Expires (days)",
      width: 130,
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    },
    {
      field: "IsActive",
      headerName: "Status",
      width: 120,
      renderCell: (params: any) => {
        const isActive = params.row.IsActive === 1;
        return (
          <Chip
            label={isActive ? "Active" : "Inactive"}
            color={isActive ? "success" : "default"}
            size="small"
            sx={{
              backgroundColor: isActive ? "#4caf50" : "#9e9e9e",
              color: "white",
              fontWeight: 500
            }}
          />
        );
      },
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    },
    {
      field: "RewardleTypeName",
      headerName: "Reward Type",
      width: 150,
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    },
    {
      field: "DateCreated",
      headerName: "Creation Date",
      width: 180,
      valueGetter: (params: any) =>
        params.row.DateCreated ? getFormattedDate(params.row.DateCreated) : "-",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 250,
      sortable: false,
      renderCell: (params: any) => {
        const isActive = params.row.IsActive === 1;
        return (
          <Box sx={{ display: "flex", gap: "8px", justifyContent: "center" }}>
            <CustomButton
              variant="contained"
              className="blue_button"
              onClick={() => handleOpenEdit(params.row)}
            >
              Edit
            </CustomButton>
            <CustomButton
              variant="contained"
              className="blue_button"
              onClick={() => handleToggleStatus(params.row)}
            >
              {isActive ? "Deactivate" : "Activate"}
            </CustomButton>
          </Box>
        );
      },
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    }
  ];

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <CustomButton
              variant="contained"
              className="blue_button"
              onClick={handleOpenCreate}
            >
              Add Reward
            </CustomButton>
            <RefreshButton onClick={fetchRewards} loading={isLoading} />
          </Box>
          <CustomDataTable
            rows={rewards}
            columns={columns}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 10 } }
            }}
            pageSizeOptions={[10, 50, 100]}
            getRowId={"id"}
            rowCount={rewards.length}
            onRowClick={() => {}}
            onPaginationModelChange={() => {}}
          />
        </Box>
      )}

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box
              component="span"
              sx={{ fontWeight: 600, fontSize: "20px", color: "#192F67" }}
            >
              {formMode === "create" ? "Create Reward" : "Edit Reward"}
            </Box>
            <IconButton
              aria-label="close"
              onClick={handleCloseModal}
              disabled={isModalLoading}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Description"
            value={formValues.Description}
            onChange={(e) =>
              setFormValues((v) => ({ ...v, Description: e.target.value }))
            }
            sx={{ mb: 2, mt: 2 }}
            disabled={isModalLoading}
          />
          <TextField
            fullWidth
            label="Points"
            type="number"
            value={formValues.Points}
            onChange={(e) =>
              setFormValues((v) => ({ ...v, Points: e.target.value }))
            }
            inputProps={{ min: 0, step: 1 }}
            sx={{ mb: 2 }}
            disabled={isModalLoading}
          />
          <TextField
            fullWidth
            label="Expiration (days)"
            type="number"
            value={formValues.ExpirationInDays}
            onChange={(e) =>
              setFormValues((v) => ({ ...v, ExpirationInDays: e.target.value }))
            }
            inputProps={{ min: 0, step: 1 }}
            sx={{ mb: 2 }}
            disabled={isModalLoading}
          />
          {formMode === "create" ? (
            <FormControl fullWidth sx={{ mb: 2 }} disabled={isModalLoading}>
              <InputLabel>Reward Type</InputLabel>
              <Select
                value={formValues.RewardleTypeId}
                label="Reward Type"
                onChange={(e) =>
                  setFormValues((v) => ({
                    ...v,
                    RewardleTypeId: Number(e.target.value)
                  }))
                }
              >
                {rewardleTypes.map((type) => (
                  <MenuItem key={type.Id} value={type.Id}>
                    {type.RewardleTypeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}

          {notificationMessage?.message && (
            <Box sx={{ mt: 1 }}>
              <Alert severity={notificationMessage?.type as any}>
                {notificationMessage?.message}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={handleCloseModal}
            disabled={isModalLoading}
            variant="contained"
            className="blue_button"
          >
            Cancel
          </CustomButton>
          <CustomButton
            onClick={handleSave}
            disabled={isModalLoading}
            variant="contained"
            className="blue_button"
          >
            {isModalLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Save"
            )}
          </CustomButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmModal.open}
        onClose={handleCloseConfirmModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box
              component="span"
              sx={{ fontWeight: 600, fontSize: "20px", color: "#192F67" }}
            >
              {confirmModal.title}
            </Box>
            <IconButton
              aria-label="close"
              onClick={handleCloseConfirmModal}
              disabled={isConfirmModalLoading}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography>{confirmModal.message}</Typography>
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={handleCloseConfirmModal}
            variant="contained"
            disabled={isConfirmModalLoading}
          >
            Cancel
          </CustomButton>
          <CustomButton
            onClick={handleConfirmToggleStatus}
            variant="contained"
            className="blue_button"
            disabled={isConfirmModalLoading}
          >
            {isConfirmModalLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : confirmModal.type === "activate" ? (
              "Activate"
            ) : (
              "Deactivate"
            )}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManageRewards;
